import {FC, useState, useEffect, useContext} from 'react'
import {IHandleFilterCallbackFunction, ILanguage} from '../../types/common'
import MultiSelectDropdown from '../../common/MultiSelectDropdown'
import YearMonthPicker from '../../common/YearMonthPicker'
import ActiveFilters from './ActiveFilters'
import {IVisibleColumnsOption} from '../../common/MultiSelectDropdown'
import axios from 'axios'
import InfoBox from '../../common/InfoBox'
import {useTranslation} from 'react-i18next'
import {SessionContext, SessionContextType} from '../../common/ctx/SessionContext'

const FiltersUFK: FC<{
    language: ILanguage
    handleFilter: IHandleFilterCallbackFunction
    ctx: any
}> = ({language, handleFilter, ctx}) => {
    const {t} = useTranslation()
    const sessionCtx: SessionContextType = useContext(SessionContext)
    const [excludedDays, setExcludedDays] = useState<Array<Date>>()
    const [selectedDate, setSelectedDate] = useState<Date | undefined>()
    const [datePickerMinDate, setDatePickerMinDate] = useState<Date | undefined>()
    const [datePickerMaxDate, setDatePickerMaxDate] = useState<Date | undefined>()
    const [selectedSegments, setSelectedSegments] = useState<Array<string>>([])
    const [selectedProducts, setSelectedProducts] = useState<Array<string>>([])
    const [searchByName, setSearchByName] = useState<string>('')
    const [segmentOptions, setSegmentOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [productOptions, setProductOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [productTooltipTxt, setProductTooltipTxt] = useState<string>('')
    const [segmentTooltipTxt, setSegmentTooltipTxt] = useState<string>('')
    const [defaultFilters, setDefaultFilters] = useState({})

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/api/filters/ufk`, {
                headers: {
                    Accept: 'application/json',
                },
                auth: {
                    username: `${process.env.REACT_APP_API_USERNAME}`,
                    password: `${process.env.REACT_APP_API_PASSWORD}`,
                },
            })
            .then((result) => {
                const filters = result.data.filters
                setSegmentOptions(filters.segment)
                setProductOptions(filters.product)
                setDefaultFilters(filters)
            })

            .catch((error) => {
                console.error(error)
            })
    }, [])

    useEffect(() => {
        ctx.isDefault()
    }, [])

    useEffect(() => {
        setSelectedSegments(ctx.segments)
        handleFilter(ctx.segments, 'segment')
    }, [ctx.segments])

    useEffect(() => {
        setSelectedProducts(ctx.products)
        handleFilter(ctx.products, 'products')
    }, [ctx.products])

    useEffect(() => {
        setDatePickerMaxDate(ctx.maxDate)
        setDatePickerMinDate(ctx.minDate)
    }, [ctx.minDate, ctx.maxDate])

    // useEffect(() => {
    //     setSelectedDate(ctx.today)
    // }, [ctx.today])

    useEffect(() => {
        setExcludedDays(ctx.holiday)
    }, [ctx.holiday])

    const handleSearch = () => {
        handleFilter(searchByName, 'name')
    }

    useEffect(() => {
        handleSearch()
    }, [searchByName])

    useEffect(() => {
        if (productOptions && ctx.products.length) {
            setProductTooltipTxt(() => {
                const filtered = productOptions.filter((item: any) => {
                    if (ctx.products.includes(item.value)) {
                        return item
                    }
                })

                return filtered.map((item) => item.label).join(', ')
            })
        }
    }, [productOptions, ctx.products])

    useEffect(() => {
        if (segmentOptions && ctx.segments.length) {
            setSegmentTooltipTxt(() => {
                const filtered = segmentOptions.filter((item: any) => {
                    if (ctx.segments.includes(item.value)) {
                        return item
                    }
                })

                return filtered.map((item) => t(item.label)).join(', ')
            })
        }
    }, [segmentOptions, ctx.segments])

    const handleSegmentChange = (value: string): void => {
        setSelectedSegments((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item !== value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setSegments((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item !== value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleProductChange = (value: string): void => {
        setSelectedProducts((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item !== value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setProducts((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    return (
        <>
            <section id="container-toolbar" className="flex">
                {sessionCtx?.getItem(ctx.STORAGE_KEY) !== 'hidden' && (
                    <InfoBox
                        title={
                            <span>
                                {' '}
                                {t('label.infobox.filters.save')} <br />
                                {t('label.infobox.filters.continue')}{' '}
                            </span>
                        }
                        onCloseCallback={() => {
                            sessionCtx.setItem(ctx.STORAGE_KEY, 'hidden')
                        }}
                    />
                )}
                <div className="filter-bar flex">
                    <div className="up-filter-bar">
                        <div className="grid-column flex">
                            <div className="datepickers-item flex">
                                <label className="datepickers-item-label">{t('label.datepicker.title2')}</label>
                                <YearMonthPicker
                                    language={language}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    datePickerMinDate={datePickerMinDate}
                                    datePickerMaxDate={datePickerMaxDate}
                                    excludeDates={excludedDays}
                                    withCurrentDate={true}
                                    filterDate={(date) => {
                                        return date.getDay() !== 0 && date.getDay() !== 6
                                    }}
                                    onDateChangeCallback={(date) => {
                                        ctx.setDate(date)
                                    }}
                                />
                            </div>
                            <div className="filter-item input-rwd">
                                <label className="filter-item-label">{t('label.segment.title')}</label>
                                <MultiSelectDropdown
                                    options={segmentOptions}
                                    selected={selectedSegments}
                                    toggleOption={handleSegmentChange}
                                    forLabel="filter_segment"
                                    title={t('label.segment.title')}
                                    tooltipTxt={t(segmentTooltipTxt)}
                                    visibletooltip={true}
                                />
                            </div>

                            <div className="filter-item input-rwd">
                                <label className="filter-item-label">{t('label.product.ufk.title')}</label>
                                <MultiSelectDropdown
                                    options={productOptions}
                                    selected={selectedProducts}
                                    toggleOption={handleProductChange}
                                    forLabel="filter_product"
                                    tooltipTxt={productTooltipTxt}
                                    visibletooltip={true}
                                />
                            </div>
                            <div className="start-search flex">
                                <label className="filter-item-label">{t('label.search.title')}</label>
                                <div className="search-bar input-rwd flex">
                                    <input
                                        className="flex"
                                        type="text"
                                        placeholder={t('label.search.placeholder')}
                                        onChange={(event) => setSearchByName(event.target.value)}
                                    />
                                    <span className="search-bar-icon-btn" onClick={handleSearch}></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <ActiveFilters ctx={ctx} filters={defaultFilters} />
            {sessionStorage?.getItem('multibonus') !== 'hidden' && (
                <InfoBox
                    title={
                        <span>
                            {t('label.infobox.multibonus')}{' '}
                            <a href="https://www.allianz.pl/pl_PL/dla-ciebie/dokumenty/zycie-i-zdrowie/multibonus.html">
                                {t('label.infobox.link')}
                            </a>
                        </span>
                    }
                    onCloseCallback={() => {
                        sessionStorage.setItem('multibonus', 'hidden')
                    }}
                />
            )}
        </>
    )
}

export default FiltersUFK
